<template>
    <div class="rf-view">
        <NavigationBar :title="$t('settings.orderHistory.title')">
            <BackButton v-if="canPop" slot="left" @click="pop" />
        </NavigationBar>
        <main>
            <h1>
                {{ $t('settings.orderHistory.title') }}
            </h1>

            <InputBox v-if="customerList.length > 0" :title="$t('settings.profiles.title')" :error-box="errorBox">
                <select v-model="selectedProfile" class="input">
                    <option v-for="customer in customerList" :key="customer.id" :value="customer.id">
                        {{ customer.settings.businessName }}
                    </option>
                </select>
            </InputBox>

            <hr>

            <Spinner v-if="loading" />

            <List v-if="!loading && groupedOrdersList.length > 0">
                <ListItem v-for="group in groupedOrdersList" :key="group.promotion?.id" :selectable="true" class="right-stack order-group">
                    <h3 class="promotion-title">{{ group.promotion?.settings.title.get($i18n) || $t('settings.orderHistory.noPromotion') }} ({{ group.orders.length }})</h3>
                    <div class="order-list">
                        <div v-for="order in group.orders" :key="order.id" class="order-item">
                            <span class="order-date">{{ formatDate(order.createdAt) }}</span>
                            <span class="order-amount">{{ $tc('settings.orderHistory.products', order.settings.amount) }}</span>
                        </div>
                    </div>
                </ListItem>
            </List>
            <p v-if="!loading && groupedOrdersList.length == 0" class="error-box">
                {{ $t('settings.orderHistory.emptyOrderList') }}
            </p>
        </main>
    </div>
</template>


<script lang="ts">
import { BackButton, InputBox, List, ListItem, NavigationBar, Radio } from "@restofrit/components";
import { Session } from "@restofrit/networking";
import { Customer, Order, Promotion, User } from "@restofrit/structures";
import { ArrayDecoder, Decoder } from "@simonbackx/simple-encoding";
import { NavigationMixin } from "@simonbackx/vue-app-navigation";
import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import { ErrorBox, Validator } from "@simonbackx/simple-error-forms";
import Spinner from "@restofrit/components/src/inputs/Spinner.vue";
import { Formatter } from "@restofrit/utilities";

@Component({
    components: {
        InputBox,
        Spinner,
        NavigationBar,
        List, 
        ListItem,
        Radio,
        BackButton
    },
})
export default class OrderHistoryView extends Mixins(NavigationMixin) {
    @Prop({ required: true })
    initialCustomer: Customer;

    loading = false;
    errorBox: ErrorBox | null = null;
    validator = new Validator();

    groupedOrdersList: { promotion: Promotion | null, orders: Order[] }[] = [];

    customerList: Customer[] = [];

    activeCustomer: Customer | null = null;

    mounted() {
        // Get customers for user
        Session.shared.authenticatedServer.request({
            method: "GET",
            path: "/customers/customers",
            decoder: new ArrayDecoder(Customer as Decoder<Customer>)
        }).then(response => {
            this.customerList = response.data;
            this.activeCustomer = this.initialCustomer || this.customerList[0];
            this.customerList.sort((a,b) => a.settings.businessName.localeCompare(b.settings.businessName));

            this.getOrders(this.activeCustomer.id);
        }).catch(error => {
            console.log(error);
        });
    }

    getOrders(profileId: string) {
        this.loading = true;
        Session.shared.authenticatedServer.request({
            method: "GET",
            path: "/dashboard/orders/" + profileId,
            decoder: new ArrayDecoder(Order as Decoder<Order>)
        }).then(response => {
            const orders = response.data ?? [];
            
            // Group orders by promotions
            const groupedOrders = orders.reduce((acc, order) => {
                if (order.settings.promotion) {
                    if (!acc[order.settings.promotion.id]) {
                        acc[order.settings.promotion.id] = {
                            promotion: order.settings.promotion,
                            orders: []
                        };
                    }
                    acc[order.settings.promotion.id].orders.push(order);
                } else {
                    // For orders without a promotion, use a special key
                    if (!acc['no_promotion']) {
                        acc['no_promotion'] = {
                            promotion: null,
                            orders: []
                        };
                    }
                    acc['no_promotion'].orders.push(order);
                }
                return acc;
            }, {} as Record<string, { promotion: Promotion | null, orders: Order[] }>);
            
            // Sort groups by the most recent order in each group
            this.groupedOrdersList = Object.values(groupedOrders).sort((a, b) => {
                const latestA = Math.max(...a.orders.map(o => o.createdAt.getTime()));
                const latestB = Math.max(...b.orders.map(o => o.createdAt.getTime()));
                return latestB - latestA;
            });
            
            // Sort orders within each group
            this.groupedOrdersList.forEach(group => {
                group.orders.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
            });
            
            this.loading = false;
        }).catch(error => {
            console.log(error);
            this.loading = false;
        });
    }

    get selectedProfile(): string {
        return this.activeCustomer ? this.activeCustomer.id : '';
    }

    set selectedProfile(profileId: string) {
        this.activeCustomer = this.customerList.find(c => c.id === profileId) || null;
        if (this.activeCustomer) {
            this.getOrders(profileId);
        }
    }

    formatDate(date: Date) {
        return Formatter.dateTime(date);
    }

}
</script>

<style lang="scss" scoped>
    .order-group {
        width: 95%;
    }

    .promotion-title {
        margin-bottom: 10px;
        color: var(--color-primary, #192DE0);
    }

    .order-list {
        padding-left: 10px;
        display: flex;
        flex-direction: column;
        gap: 5px;
    }

    .order-item {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        color: var(--color-text-secondary, #464a51);
        border-bottom: 1px solid var(--color-border-secondary, #e0e0e0);
    }

    .order-date {
        width: 165px;
    }

    .order-amount {
        font-weight: 600;
    }

</style>